import { themeVar } from '@/styles/theme';
import React, { useState } from 'react';
import styled from 'styled-components';

import { DueDiligencePageGetDueDiligenceRecordQuery, DueDiligenceRecordStatus, DueDiligenceRecordTierStatus } from '@/__generated__/graphql';
import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { ScoreArrow } from '@/assets/icons/scoreArrow';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import Button from '@/components/basicComponents/button';
import { useMutation } from '@apollo/client';
import { useResponsive } from '../../../hooks/use-responsive';
import { DUE_DILIGENCE_RECORD_QUERY, TOGGLE_SECTION_COMPLETION_MUTATION, UPDATE_DUE_DILIGENCE_RECORD_SECTION_SCORE_MUTATION } from '../queries';
import { ChangeScore } from './changeScore';
import SectionStatus from './sectionStatus';

interface TierDropdownProps {
  children: React.ReactNode;
  title: string;
  bgColor?: string;
  openNotes?: (
    note: string,
    updateAt: Date,
    task: string,
    assignedBy:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['data']['assignedBy']
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]['data']['assignedBy'],
    assignedTo:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['data']['assignedTo']
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]['data']['assignedTo']
  ) => void;
  openModalWindow?: (
    type: string,
    row:
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]
      | DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'][number]
  ) => void;
  sectionData?: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number];
  checkListData?: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['checklistItems'];
  tierStatus?: DueDiligenceRecordTierStatus;
  owner?: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['owner'];
  prevTierStatus?: DueDiligenceRecordTierStatus | null;
  isBackToLoading?: boolean;
  recordStatus?: DueDiligenceRecordStatus | null;
}

export const TierDropdown = ({
  children,
  title,
  bgColor,
  openNotes,
  openModalWindow,
  sectionData,
  tierStatus,
  checkListData,
  owner,
  prevTierStatus,
  isBackToLoading = false,
  recordStatus
}: TierDropdownProps) => {
  const { isMobile } = useResponsive();

  const [isDropdown, setIsDropdown] = useState(true);
  const [currentScore, setCurrentScore] = useState(sectionData?.data.score || 0);
  const [isScoreOpen, setIsScoreOpen] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);

  const [updateDueDiligenceRecordSectionScore] = useMutation(UPDATE_DUE_DILIGENCE_RECORD_SECTION_SCORE_MUTATION);
  const [updateSectionCompletion, { loading: updateSectionCompletionLoading }] = useMutation(TOGGLE_SECTION_COMPLETION_MUTATION, {
    refetchQueries: [DUE_DILIGENCE_RECORD_QUERY],
    awaitRefetchQueries: true
  });

  const updateScore = (count: number) => {
    updateDueDiligenceRecordSectionScore({
      variables: {
        data: {
          id: sectionData?.data.id,
          score: count
        }
      },
      onCompleted: (data: any) => {
        setCurrentScore(data.updateDueDiligenceRecordSectionScore.score);
      }
    });
  };

  const handleChangeSectionStatus = (isCompleted: boolean) => {
    updateSectionCompletion({
      variables: {
        data: {
          id: sectionData?.data.id,
          completed: isCompleted
        }
      }
    });
  };

  const isSectionFieldsValue = sectionData?.fields.filter((item) => {
    if (item.required && !item.data.value && !item.data.commentary && !item.data.attachments.length && !item.AI?.answers.length) {
      return item;
    }
  });

  const isDisabled =
    sectionData?.data.completed ||
    prevTierStatus === DueDiligenceRecordTierStatus.Rejected ||
    recordStatus === DueDiligenceRecordStatus.Approved ||
    recordStatus === DueDiligenceRecordStatus.Rejected;

  return (
    <DropdownWrapper>
      <DropdownHeader isMobile={isMobile} isChecklist={Boolean(openModalWindow)} isDropdown={isDropdown}>
        <HeaderInfo>
          <Title isMobile={isMobile} id="dropdownTitle">
            {title}
          </Title>
          {openModalWindow && openNotes && owner && recordStatus && (
            <SectionStatus
              prevTierStatus={prevTierStatus ?? null}
              owner={owner}
              isSection={true}
              currentRow={sectionData}
              sectionTitle={title}
              openModalWindow={openModalWindow}
              openNotes={openNotes}
              loading={isBackToLoading}
              recordStatus={recordStatus}
            />
          )}
        </HeaderInfo>
        <ArrowScoreWrapper isMobile={isMobile} isChecklist={Boolean(openModalWindow)}>
          {sectionData?.userCanScore && owner && (
            <>
              <ScoreCounter isMobile={isMobile} disabled={isDisabled} onClick={isDisabled ? () => null : () => setIsScoreOpen(!isScoreOpen)}>
                {currentScore === 0 && (
                  <>
                    <ScoreArrow width={isMobile ? 25 : 34} height={isMobile ? 25 : 34} fill={themeVar('layer.3')} />
                    <ScoreWrapper>-</ScoreWrapper>
                  </>
                )}
                {currentScore > 0 && (
                  <>
                    <ScoreArrow
                      width={isMobile ? 25 : 34}
                      height={isMobile ? 25 : 34}
                      fill={themeVar('context.success')}
                      style={{ transform: 'rotate(-90deg)' }}
                    />
                    <ScoreWrapper color={themeVar('context.success')}>+{currentScore}</ScoreWrapper>
                  </>
                )}
                {currentScore < 0 && (
                  <>
                    <ScoreArrow
                      width={isMobile ? 25 : 34}
                      height={isMobile ? 25 : 34}
                      fill={themeVar('context.error')}
                      style={{ transform: 'rotate(90deg)' }}
                    />
                    <ScoreWrapper color={themeVar('context.error')}>{currentScore}</ScoreWrapper>
                  </>
                )}
              </ScoreCounter>
              {isScoreOpen && (
                <>
                  {!isDisabled && (
                    <ChangeScore
                      updateScore={(count) => updateScore(count)}
                      count={currentScore}
                      setCount={setCurrentScore}
                      onClose={setIsScoreOpen}
                    />
                  )}
                </>
              )}
            </>
          )}
          <ArrowWrapper
            isMobile={isMobile}
            onClick={() => setIsDropdown(!isDropdown)}
            onMouseEnter={() => setArrowHover(true)}
            onMouseLeave={() => setArrowHover(false)}
          >
            <>
              {isDropdown ? (
                <ArrowUp style={{ cursor: 'pointer' }} fill={arrowHover && !isMobile ? themeVar('context.light') : themeVar('font.action')} />
              ) : (
                <ArrowDown
                  style={{
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    padding: '7px'
                  }}
                  fill={arrowHover && !isMobile ? themeVar('context.light') : themeVar('font.action')}
                />
              )}
            </>
          </ArrowWrapper>
        </ArrowScoreWrapper>
      </DropdownHeader>
      <DropdownBody isMobile={isMobile} isDropdown={isDropdown} bgColor={bgColor}>
        <ExpandableWrapp isMobile={isMobile} isDropdown={isDropdown}>
          {children}
          {owner && tierStatus === DueDiligenceRecordTierStatus.InProgress && (
            <>
              {sectionData?.data.completed ? (
                <ButtonWrapper>
                  <Button
                    onClick={() => handleChangeSectionStatus(false)}
                    styleType={updateSectionCompletionLoading ? 'disabled' : 'outline'}
                    isLoading={updateSectionCompletionLoading}
                    size="lg"
                  >
                    Reopen Task
                  </Button>
                </ButtonWrapper>
              ) : (
                <ButtonWrapper>
                  <Button
                    onClick={() => handleChangeSectionStatus(true)}
                    styleType={Boolean(isSectionFieldsValue?.length) || updateSectionCompletionLoading ? 'disabled' : 'default'}
                    isLoading={updateSectionCompletionLoading}
                    size="lg"
                  >
                    Mark As Complete
                  </Button>
                </ButtonWrapper>
              )}
            </>
          )}
        </ExpandableWrapp>
      </DropdownBody>
    </DropdownWrapper>
  );
};

export const DropdownWrapper = styled.div`
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const ScoreWrapper = styled.div<{ color?: string }>`
  padding: 1px 10px;
  font-weight: 700;
  font-size: 13px;
  background-color: ${({ color, theme }) => (color ? color : themeVar('layer.1'))};
  display: inline-block;
  border-radius: 12px;
  color: ${({ color, theme }) => (color ? themeVar('context.light') : themeVar('font.strong'))};
  margin-right: 6px;
`;

export const DropdownHeader = styled.div<{ isMobile: boolean; isChecklist: boolean; isDropdown: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ isMobile, isChecklist }) => (isMobile && isChecklist ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile, isChecklist }) => (isMobile && isChecklist ? 'column' : 'row')};
  padding: ${({ isMobile }) => (isMobile ? '15px 20px' : '20px 40px')};
  position: relative;
  border-radius: 12px 12px 0 0;
  background-color: ${themeVar('layer.base')};

  &:hover {
    background-color: ${themeVar('layer.2')};
    transition: background-color 100ms ease;
    border-radius: ${({ isDropdown }) => (isDropdown ? '12px 12px 0 0' : '12px')};
  }
`;

export const ArrowWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border-radius: 25px;
  cursor: pointer;

  &:hover {
    background-color: ${({ isMobile }) => (isMobile ? 'transparent' : themeVar('action.hover'))};
    transition: background-color 100ms ease;
  }
`;

export const Title = styled.div<{ isMobile: boolean }>`
  font-family: Lato;
  font-weight: 700;
  font-size: ${({ isMobile }) => (isMobile ? '20px' : '28px')};
  line-height: ${({ isMobile }) => (isMobile ? '30px' : '22px')};
  color: ${themeVar('font.strong')};
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const DropdownBody = styled.div<{ isMobile: boolean; isDropdown: boolean; bgColor?: string }>`
  background-color: ${({ bgColor }) => (bgColor ? bgColor : themeVar('layer.1'))};
  border-radius: 0 0 10px 10px;
  border-top: ${({ isDropdown }) => (isDropdown ? `1px solid ${themeVar('border.base')}` : 'none')};
  opacity: ${({ isDropdown }) => (isDropdown ? '1' : '0')};
  display: grid;
  grid-template-rows: ${({ isDropdown }) => (isDropdown ? '1fr' : '0fr')};
  overflow: ${({ isDropdown }) => (isDropdown ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

export const ExpandableWrapp = styled.div<{ isMobile: boolean; isDropdown: boolean }>`
  min-height: 0;
  padding: ${({ isMobile, isDropdown }) => (isMobile && isDropdown ? '20px' : isDropdown ? '40px' : 0)};
`;

const ArrowScoreWrapper = styled.div<{ isMobile: boolean; isChecklist: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ isMobile }) => (isMobile ? 'space-between' : 'flex-start')};
  margin-top: ${({ isMobile }) => (isMobile ? '10px' : '0')};
  gap: ${({ isMobile }) => (isMobile ? '0' : '30px')};
  width: ${({ isMobile, isChecklist }) => (isMobile && isChecklist ? '100%' : 'auto')};
  position: relative;
`;

const ScoreCounter = styled.div<{ disabled: boolean; isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${themeVar('layer.1')};
  border-radius: 32px;
  padding: 8px;
  margin-left: ${({ isMobile }) => (isMobile ? '0' : 'auto')};
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 30px 0 20px 0;
`;
